import { useRef, useState } from "react"
import { Link } from "react-router"
import { openBillingPortal, deleteAccount, updateEmailPreferences } from "../lib/api"
import useUser from "../hooks/useUser"
import useUpgrade from "../hooks/useUpgrade"
import NotificationsSection from "./NotificationsSection"

// Authenticated profile page at /app/profile.
//
// Identity (name/email/avatar/provider) is read-only: the login flow
// re-upserts it from the Supabase OAuth token on every sign-in, so an
// in-app edit form would be silently overwritten. Plan/billing reuses the
// existing checkout + customer-portal flows. Sign-out lives in the site
// header / hamburger menu, not here.
// See docs/superpowers/specs/2026-05-20-user-profile-design.md.

function providerLabel(provider) {
  if (!provider || provider === "email") return "email"
  return provider.charAt(0).toUpperCase() + provider.slice(1)
}

function formatDate(iso) {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
}

export default function Profile() {
  const { status, user } = useUser()
  // The pricing box, not a direct monthly checkout (#336).
  const { upgrade } = useUpgrade()
  const [billingBusy, setBillingBusy] = useState(false)
  const [error, setError] = useState(null)

  if (status === "loading" || !user) {
    return (
      <div className="max-w-2xl mx-auto py-8 px-4 text-center text-[color:var(--color-muted)] dark:text-muted-light text-sm">
        Loading…
      </div>
    )
  }

  const isPaid = !!user.entitlements?.paid
  const isSuperuser = !!user.entitlements?.superuser
  const plan = user.entitlements?.plan
  const subscription = user.subscription

  async function handleManageBilling() {
    setBillingBusy(true)
    setError(null)
    try {
      await openBillingPortal() // navigates away on success
    } catch (e) {
      setBillingBusy(false)
      setError(e.message || "Couldn't open the billing portal. Try again.")
    }
  }

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-display font-semibold text-xl text-ink dark:text-cloud">Profile</h1>
        <Link to="/app" className="text-sm text-voltage hover:text-voltage-dark dark:hover:text-voltage-light transition">
          ← Your codes
        </Link>
      </div>

      {error && (
        <div className="border border-fail rounded p-4 text-fail text-sm mb-5">{error}</div>
      )}

      {/* Identity — read-only, sourced from the OAuth provider. */}
      <section className="border border-[color:var(--color-border)] dark:border-storm rounded-lg bg-white dark:bg-midnight p-5 mb-5">
        <div className="flex items-center gap-4">
          {user.avatar_url ? (
            <img
              src="/me/avatar"
              alt=""
              className="h-14 w-14 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="h-14 w-14 rounded-full bg-[color:var(--color-cloud)] dark:bg-coal flex items-center justify-center text-lg font-display font-semibold text-[color:var(--color-muted)] dark:text-muted-light flex-shrink-0">
              {(user.full_name || user.email || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            {user.full_name && (
              <div className="font-display font-semibold text-ink dark:text-cloud truncate">{user.full_name}</div>
            )}
            <div className="text-sm text-[color:var(--color-muted)] dark:text-muted-light font-mono truncate">
              {user.email}
            </div>
            <div className="mt-1 inline-flex items-center gap-1.5 text-xs text-[color:var(--color-muted)] dark:text-muted-light">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-voltage" aria-hidden="true" />
              Signed in with {providerLabel(user.provider)}
            </div>
          </div>
        </div>
        <p className="text-xs text-[color:var(--color-muted)] dark:text-muted-light mt-4">
          Your name, email, and photo come from your sign-in provider. To change them, update them
          with {providerLabel(user.provider)}.
        </p>
      </section>

      {/* Plan & billing. */}
      <section className="border border-[color:var(--color-border)] dark:border-storm rounded-lg bg-white dark:bg-midnight p-5 mb-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-[color:var(--color-muted)] dark:text-muted-light">Plan</div>
            <div className="font-display font-semibold text-ink dark:text-cloud mt-0.5">
              {isSuperuser ? "Staff — full access" : isPaid ? "Pro" : "Free"}
            </div>
            <PlanDetail
              isSuperuser={isSuperuser}
              isPaid={isPaid}
              subscription={subscription}
            />
          </div>
          {!isSuperuser && (
            isPaid ? (
              <button
                type="button"
                onClick={handleManageBilling}
                disabled={billingBusy}
                className="border border-[color:var(--color-border)] dark:border-storm text-ink dark:text-cloud rounded-lg px-3 py-2 text-sm font-medium hover:border-voltage transition disabled:opacity-50 flex-shrink-0"
              >
                {billingBusy ? "Opening…" : "Manage billing"}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => upgrade()}
                className="bg-voltage text-coal rounded-lg px-3 py-2 text-sm font-medium hover:bg-voltage-dark transition disabled:opacity-50 flex-shrink-0"
              >
                Upgrade to Pro
              </button>
            )
          )}
        </div>
      </section>

      <EmailPreferencesSection initialConsent={user.marketing_consent !== false} />

      <NotificationsSection />

      <DeleteAccountSection isPaid={isPaid} />
    </div>
  )
}

// The in-app half of the unsubscribe promise on /privacy ("using the
// unsubscribe link in any email or from your account settings"). The other two
// paths are the visible link in every broadcast and the RFC 8058 one-click
// header; all three land on the same EmailPreference.opt_out!.
//
// Optimistic, with a rollback on failure: this is a preference toggle, and
// making someone wait on a round trip to see their own click register is worse
// than the rare snap-back. The server's response is authoritative — a
// suppressed (bounced) address stays off no matter what the switch says.
function EmailPreferencesSection({ initialConsent }) {
  const [consent, setConsent] = useState(initialConsent)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  async function toggle() {
    const next = !consent
    setConsent(next)
    setSaving(true)
    setError(null)
    try {
      const result = await updateEmailPreferences(next)
      setConsent(result.marketing_consent)
    } catch (e) {
      setConsent(!next)
      setError(e.message || "Couldn't save that. Try again.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="border border-[color:var(--color-border)] dark:border-storm rounded-lg bg-white dark:bg-midnight p-5 mb-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-[color:var(--color-muted)] dark:text-muted-light">
            Email preferences
          </div>
          <label htmlFor="marketing-consent" className="font-display font-semibold text-ink dark:text-cloud mt-0.5 block cursor-pointer">
            Receive product updates and announcements.
          </label>
          <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1">
            Every couple of months at most. Email about codes you own, like
            confirmations and receipts, keeps coming either way.
          </p>
        </div>
        <input
          id="marketing-consent"
          type="checkbox"
          checked={consent}
          onChange={toggle}
          disabled={saving}
          className="mt-1 h-5 w-5 flex-shrink-0 accent-[color:var(--color-voltage)] cursor-pointer disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 dark:focus-visible:ring-offset-midnight"
        />
      </div>

      {error && (
        <div className="border border-fail rounded p-3 text-fail text-sm mt-4">{error}</div>
      )}
    </section>
  )
}

// Danger zone: irreversible account deletion behind a type-to-confirm modal.
// The user types DELETE to enable the destructive button. On success the
// session is gone, so we hard-navigate to the marketing home.
function DeleteAccountSection({ isPaid }) {
  const dialogRef = useRef(null)
  const [confirmText, setConfirmText] = useState("")
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)

  const canDelete = confirmText.trim().toUpperCase() === "DELETE"

  function open() {
    setConfirmText("")
    setError(null)
    dialogRef.current?.showModal()
  }

  function close() {
    if (!deleting) dialogRef.current?.close()
  }

  async function handleDelete() {
    if (!canDelete) return
    setDeleting(true)
    setError(null)
    try {
      await deleteAccount()
      window.location.assign("/") // signed out — leave the SPA
    } catch (e) {
      setDeleting(false)
      setError(e.message || "Couldn't delete your account. Try again.")
    }
  }

  return (
    <section className="border border-fail/40 rounded-lg bg-white dark:bg-midnight p-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-fail">Danger zone</div>
          <div className="font-display font-semibold text-ink dark:text-cloud mt-0.5">Delete account</div>
          <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1">
            Permanently erases your account and personal data. Your QR codes keep working.
          </p>
        </div>
        <button
          type="button"
          onClick={open}
          className="border border-fail text-fail rounded-lg px-3 py-2 text-sm font-medium hover:bg-fail/5 transition flex-shrink-0"
        >
          Delete account
        </button>
      </div>

      <dialog
        ref={dialogRef}
        className="rounded-lg p-0 w-full max-w-md bg-white dark:bg-midnight backdrop:bg-black/40"
        onCancel={(e) => { if (deleting) e.preventDefault() }}
      >
        <div className="p-6">
          <h2 className="font-display font-semibold text-lg text-ink dark:text-cloud">Delete your account?</h2>
          <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-2">This can't be undone. Here's what happens:</p>
          <ul className="mt-2 text-sm text-[color:var(--color-muted)] dark:text-muted-light list-disc pl-5 space-y-1">
            <li>Your name, email, and profile are erased.</li>
            {isPaid && <li>Your Pro subscription is canceled immediately (no further charges).</li>}
            <li><strong className="text-ink dark:text-cloud">Your QR codes keep redirecting</strong> — anything you've printed stays working.</li>
          </ul>

          {error && (
            <div className="border border-fail rounded p-3 text-fail text-sm mt-4">{error}</div>
          )}

          <label htmlFor="delete-confirm" className="block text-sm font-medium text-ink dark:text-cloud mt-5">
            Type <span className="font-mono">DELETE</span> to confirm
          </label>
          <input
            id="delete-confirm"
            type="text"
            autoComplete="off"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-[color:var(--color-border)] dark:border-storm rounded text-ink dark:text-cloud font-mono focus:outline-none focus-visible:ring-2 focus-visible:ring-voltage focus-visible:ring-offset-2 dark:focus-visible:ring-offset-midnight"
          />

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={close}
              disabled={deleting}
              className="text-sm text-[color:var(--color-muted)] dark:text-muted-light hover:text-ink dark:hover:text-cloud transition disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={!canDelete || deleting}
              className="border border-fail text-fail rounded-lg px-4 py-2 text-sm font-medium hover:bg-fail/5 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {deleting ? "Deleting…" : "Delete my account"}
            </button>
          </div>
        </div>
      </dialog>
    </section>
  )
}

function PlanDetail({ isSuperuser, isPaid, subscription }) {
  if (isSuperuser) {
    return (
      <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1">
        Billing doesn't apply to staff accounts.
      </p>
    )
  }
  if (!isPaid) {
    return (
      <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1">
        Every code gets full editing and detailed analytics for its first 50 scans.
      </p>
    )
  }

  const status = subscription?.status
  if (status === "past_due" || status === "unpaid") {
    return (
      <p className="text-sm text-fail mt-1">
        Your last payment failed. Update your card to keep Pro; we're still retrying.
      </p>
    )
  }

  const cancelDate = formatDate(subscription?.cancel_at)
  const renewDate = formatDate(subscription?.current_period_end)
  if (cancelDate) {
    return <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1">Cancels {cancelDate}</p>
  }
  if (renewDate) {
    return <p className="text-sm text-[color:var(--color-muted)] dark:text-muted-light mt-1">Renews {renewDate}</p>
  }
  return null
}
